import React, { createContext, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { HiX } from 'react-icons/hi';
import Button from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'screen-2/3';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  closeDisabled?: boolean;
  footer?: React.ReactNode;
  zIndex?: number;
}

const ModalPortalContext = createContext<HTMLElement | null | undefined>(undefined);

// Floating controls belong to their dialog's focus and accessibility boundary.
export function ModalPortal({ children }: { children: React.ReactNode }) {
  const container = useContext(ModalPortalContext);
  return container === null ? null : createPortal(children, container ?? document.body);
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  closeDisabled = false,
  footer,
  zIndex = 50,
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const isMouseDownOnOverlay = useRef(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    '3xl': 'max-w-7xl',
    '4xl': 'max-w-screen-xl',
    '5xl': 'max-w-screen-2xl',
    'screen-2/3': 'max-w-none sm:w-2/3',
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    isMouseDownOnOverlay.current = event.target === overlayRef.current;
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isMouseDownOnOverlay.current && event.target === overlayRef.current) {
      if (closeOnOverlayClick && !closeDisabled) {
        onClose();
      }
    }
    isMouseDownOnOverlay.current = false;
  };

  return <Dialog.Root open={isOpen} onOpenChange={open => { if (!open && !closeDisabled) onClose(); }}>
    <Dialog.Portal><Dialog.Overlay
      ref={overlayRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <Dialog.Content ref={setContainer} aria-modal="true" aria-describedby={undefined}
        className={classNames('modal-content flex flex-col min-w-0', sizeClasses[size])}
        onOpenAutoFocus={() => { previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; }}
        onCloseAutoFocus={event => {
          event.preventDefault();
          // Close restoration is deferred; do not override a newer interaction.
          if (document.activeElement === document.body && previousFocus.current?.isConnected) previousFocus.current.focus();
        }}
        onEscapeKeyDown={event => { if (!closeOnEscape || closeDisabled) event.preventDefault(); }}
        onPointerDownOutside={event => event.preventDefault()}
      ><ModalPortalContext.Provider value={container}>
        {!title && <Dialog.Title className="sr-only">{t('modal.title')}</Dialog.Title>}
        {(title || showCloseButton) && (
          <div className="flex shrink-0 items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-200">
            {title && (
              <Dialog.Title className="min-w-0 break-words text-xl font-semibold text-gray-900">{title}</Dialog.Title>
            )}
            {showCloseButton && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={onClose}
                disabled={closeDisabled}
                aria-label={t('buttons.close')}
                title={t('buttons.close')}
                icon={<HiX className="h-4 w-4" />}
                className="ml-auto shrink-0 min-h-[44px] min-w-[44px] border-none hover:bg-gray-100"
              />
            )}
          </div>
        )}

        <div className="min-h-0 p-4 sm:p-6 overflow-y-auto">
          {children}
        </div>

        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
            {footer}
          </div>
        )}
      </ModalPortalContext.Provider></Dialog.Content>
    </Dialog.Overlay></Dialog.Portal>
  </Dialog.Root>;
};

export default Modal;
